/* RunIt wishlist — Shopify account sync.
 *
 * The on-device list (assets/runit-wishlist-core.js) is the guest wishlist and
 * stays exactly as it was. This file is the second half: for a shopper who is
 * signed in, the list is kept in a per-customer cache on their own device, and —
 * when an endpoint is configured — mirrored to a Shopify customer metafield
 * through an app proxy, so it follows them to every device.
 *
 *         browser                          Shopify                 our server
 *   ┌───────────────────┐                                            (worker)
 *   │ localStorage      │  guest list, unchanged
 *   │ runit:wishlist:v1 │
 *   ├───────────────────┤
 *   │ acct:<customerId> │  cache of the metafield, per customer
 *   │ queue:<customerId>│  writes the server has not acknowledged yet
 *   └─────────┬─────────┘
 *             │  GET  <endpoint>/sync    ── app proxy ─►  /wishlist/sync
 *             │  POST <endpoint>/add     ──────────────►  /wishlist/add
 *             │  POST <endpoint>/remove  ──────────────►  /wishlist/remove
 *             │  POST <endpoint>/merge   ──────────────►  /wishlist/merge
 *
 * `<endpoint>` is supplied by whoever builds the client, and the theme supplies
 * the RUN IT OS app proxy's wishlist route (`data-sync-url` in
 * snippets/runit-wishlist.liquid). RUN IT OS is the one dedicated app this store
 * runs for its own systems: Blackjack lives under /apps/runit/blackjack/* and
 * the wishlist under /apps/runit/wishlist/*. Reading an empty string here is
 * what puts this module in its `off` state below — no fetch, no queue, no sync
 * status, list kept locally — which is what a guest gets.
 *
 * Two rules shape everything below.
 *
 * 1. The browser never says who it is. The proxy attaches a signed
 *    `logged_in_customer_id` to the request and the worker only trusts that, so
 *    there is no customer id in any payload here and no credential anywhere in the
 *    theme. The id Liquid renders is used for ONE thing: naming this shopper's
 *    local cache and queue, so two accounts on a shared device cannot read each
 *    other's list.
 *
 * 2. A missing or broken endpoint must cost the shopper nothing — which is not a
 *    hypothetical: this module shipped for months against a proxy path that had
 *    no backend at all, so every request 404'd, and the only correct behaviour was
 *    to go quiet. An absent endpoint (state `off`) and an unreachable one (state
 *    `unknown`, then `down`) are handled the same way: the list keeps working from
 *    the local cache, changes are queued, and nothing is lost when an endpoint
 *    appears later. It never shows an error for a backend it cannot reach.
 *
 * Split from runit-wishlist.js the same way runit-wishlist-core.js is: this file
 * is network + state, no DOM, so the merge/queue/identity rules are unit-tested in
 * tests/runit-wishlist-account.test.js with a fake fetch.
 */
(function (root, factory) {
  var core = root && root.RunItWishlistCore;
  var api = factory(core);
  if (typeof module === 'object' && module.exports) {
    module.exports = api;
  }
  if (root) {
    root.RunItWishlistAccount = api;
  }
})(typeof globalThis !== 'undefined' ? globalThis : this, function (injectedCore) {
  'use strict';

  var DEFAULT_TIMEOUT = 12000;
  var FLUSH_DELAY = 350;
  var MAX_ATTEMPTS = 5;

  function create(options) {
    var opts = options || {};
    var core = opts.core || injectedCore;
    if (!core) throw new Error('RunItWishlistCore is required');

    var storage = opts.storage;
    var doFetch = opts.fetch || (typeof fetch === 'function' ? fetch.bind(globalThis) : null);
    var now = opts.now || function () { return Date.now(); };
    var endpoint = String(opts.endpoint || '').replace(/\/+$/, '');
    var customerId = opts.customerId ? String(opts.customerId) : '';
    var timeoutMs = opts.timeoutMs || DEFAULT_TIMEOUT;
    /* localStorage in private mode throws; without it we can still sync this page. */
    var memory = {};

    var mode = customerId ? 'account' : 'guest';
    var endpointUsable = !!endpoint && !!doFetch;
    var listeners = [];
    /* The account's deletions (`[{ productId, removedAt }]`). Liquid renders them
     * with the list, and every response carries them, so a merge always knows
     * what has been removed — and a local copy of a removed item is never drawn
     * and never uploaded. Empty means "nothing has been removed", which is also
     * what an older backend that does not send the field means. */
    var removals = core.sanitiseTombstones(opts.initialRemovals);
    var state = {
      mode: mode,
      syncing: false,
      pending: 0,
      /* null while the account list is unknown. Never merge into an unknown list:
       * that is how one shopper's items end up on another shopper's account. */
      account: null,
      removed: removals.slice(),
      stale: false,
      error: '',
      server: endpointUsable ? 'unknown' : 'off'
    };
    var inFlight = null;
    var flushTimer = null;
    var flushing = false;

    function read(key) {
      if (storage) return core.readJson(storage, key, null);
      return Object.prototype.hasOwnProperty.call(memory, key) ? JSON.parse(memory[key]) : null;
    }

    function write(key, value) {
      if (storage) return core.writeJson(storage, key, value);
      memory[key] = JSON.stringify(value);
      return true;
    }

    function drop(key) {
      if (storage) return core.removeKey(storage, key);
      delete memory[key];
      return true;
    }

    function emit() {
      state.pending = mode === 'account' ? queue().length : 0;
      for (var i = 0; i < listeners.length; i++) {
        try {
          listeners[i](state);
        } catch (e) {}
      }
    }

    function queue() {
      if (mode !== 'account') return [];
      return core.sanitiseQueue(read(core.queueKey(customerId)));
    }

    function setQueue(ops) {
      if (mode !== 'account') return [];
      if (storage) return core.writeQueue(storage, customerId, ops);
      var clean = core.sanitiseQueue(ops);
      if (!clean.length) drop(core.queueKey(customerId));
      else write(core.queueKey(customerId), clean);
      return clean;
    }

    function cached() {
      if (mode !== 'account') return [];
      var raw = read(core.cacheKey(customerId));
      return core.sanitise(raw || []);
    }

    function setCache(items) {
      if (mode !== 'account') return;
      write(core.cacheKey(customerId), core.sanitise(items));
    }

    /* The server decides which products are on the account; the cache keeps the
     * display fields this device already paid for (title, price, image) so an
     * offline load still draws real rows. */
    function setCacheFromAccount(items) {
      var rows = core.cacheFromAccountItems(items, cached());
      write(core.cacheKey(customerId), rows);
      return rows;
    }

    function merged() {
      return read(core.mergedKey(customerId)) === '1';
    }

    function setMerged(value) {
      if (value) write(core.mergedKey(customerId), '1');
      else drop(core.mergedKey(customerId));
    }

    /* ------------------------------------------------------------- transport */

    /* One request. Never throws: a wishlist write must not be able to break a page
     * or surface a stack trace to a shopper. */
    function request(path, body) {
      if (!endpointUsable) return Promise.resolve({ ok: false, why: 'off' });
      var url = endpoint + path;
      var init = {
        method: body ? 'POST' : 'GET',
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
        signal: null
      };
      var timer = null;
      if (typeof AbortController === 'function') {
        var controller = new AbortController();
        init.signal = controller.signal;
        timer = setTimeout(function () {
          try { controller.abort(); } catch (e) {}
        }, timeoutMs);
      }
      if (body) {
        init.headers['Content-Type'] = 'application/json';
        init.body = JSON.stringify(body);
      }
      return doFetch(url, init)
        .then(function (response) {
          if (timer) clearTimeout(timer);
          /* 404/501 is not a transient failure: the app proxy is not configured
           * (or the worker has no such route). Stop asking for the rest of the
           * page's life and keep going on the local list — the same reasoning as
           * the storefront's other dead-endpoint guard. */
          if (response.status === 404 || response.status === 501) {
            endpointUsable = false;
            state.server = 'off';
            return { ok: false, why: 'off' };
          }
          if (response.status === 401 || response.status === 403) {
            return { ok: false, why: 'signed-out', status: response.status };
          }
          if (!response.ok) return { ok: false, why: 'http-' + response.status };
          return response.json().then(
            function (data) { return { ok: true, data: data || {} }; },
            function () { return { ok: false, why: 'bad-json' }; }
          );
        })
        .catch(function () {
          if (timer) clearTimeout(timer);
          return { ok: false, why: 'network' };
        });
    }

    function itemsFrom(result) {
      if (!result || !result.ok) return null;
      var data = result.data || {};
      var list = Array.isArray(data) ? data : data.items;
      return Array.isArray(list) ? core.sanitiseAccountList(list) : null;
    }

    /* The deletions that came back with a list. null (rather than []) when the
     * answer carried none at all, so an older server cannot silently wipe what
     * Liquid already told us was removed. */
    function removalsFrom(result) {
      if (!result || !result.ok) return null;
      var data = result.data || {};
      if (Array.isArray(data)) return null;
      return Array.isArray(data.removed) ? core.sanitiseTombstones(data.removed) : null;
    }

    function setRemovals(next) {
      if (!next) return;
      removals = next;
      state.removed = next.slice();
    }

    /* ----------------------------------------------------------------- reads */

    /* The authoritative account list. Liquid may already have rendered it (the
     * customer metafield is readable server-side), which is why `opts.initialItems`
     * is used for first paint — but the endpoint is asked once per page too, so a
     * change made on another device shows up here without a second page load. */
    function load(single) {
      if (mode !== 'account') return Promise.resolve({ ok: false, why: 'guest' });
      if (single && inFlight) return inFlight;
      state.syncing = true;
      emit();
      var promise = request('/sync', null).then(function (result) {
        state.syncing = false;
        var items = itemsFrom(result);
        if (items) {
          state.account = items;
          setRemovals(removalsFrom(result));
          state.server = 'up';
          state.stale = false;
          state.error = '';
          setCacheFromAccount(items);
        } else {
          state.server = result.why === 'off' ? 'off' : 'down';
          state.error = result.why === 'off' ? '' : result.why;
          state.stale = true;
        }
        emit();
        return { ok: !!items, items: items, why: result.why };
      });
      if (single) inFlight = promise;
      /* A failed sync frees the single-flight slot, so the next page or interaction
       * can try again rather than being stuck with a rejected promise. */
      promise.then(function () {
        if (inFlight === promise) inFlight = null;
      });
      return promise;
    }

    /* ----------------------------------------------------------------- writes */

    function enqueue(op) {
      if (mode !== 'account') return false;
      setQueue(core.queueOperation(queue(), op));
      emit();
      return true;
    }

    function scheduleFlush() {
      if (!endpointUsable || mode !== 'account') return;
      if (flushTimer) clearTimeout(flushTimer);
      flushTimer = setTimeout(function () {
        flushTimer = null;
        flush();
      }, FLUSH_DELAY);
    }

    /* Send queued operations in order, one at a time. Each success drops that
     * operation; a network failure keeps the rest of the queue for the next page
     * (the local list is already correct, so nothing looks broken meanwhile). The
     * server's response is the authoritative list and replaces the cache. */
    function flush() {
      if (mode !== 'account' || flushing || !endpointUsable) return Promise.resolve(false);
      var ops = queue();
      if (!ops.length) return Promise.resolve(true);
      flushing = true;
      var index = 0;

      function next() {
        if (index >= ops.length) {
          flushing = false;
          emit();
          return true;
        }
        var op = ops[index];
        var path = op.type === 'add' ? '/add' : op.type === 'remove' ? '/remove' : '/merge';
        var body = op.type === 'merge' ? { items: op.items } : op.type === 'add' ? { item: op.item } : { productId: op.productId };
        return request(path, body).then(function (result) {
          if (result.ok) {
            var items = itemsFrom(result);
            if (items) {
              state.account = items;
              /* A successful write is where a NEW removal appears, so the
               * removals are taken from every answer, not only the read. */
              setRemovals(removalsFrom(result));
              setCacheFromAccount(items);
            }
            if (op.type === 'merge') {
              /* The server has the merged list. Only now is it safe to stop
               * holding the guest copy: until this point a failed merge had to
               * leave the shopper's items on the device as well. */
              setMerged(true);
              drop(core.KEY);
            }
            /* Drop only what was acknowledged, by product: anything queued while
             * this request was in the air survives. */
            setQueue(queue().filter(function (entry) { return entry.productId !== op.productId; }));
            index++;
            emit();
            return next();
          }
          flushing = false;
          if (result.why === 'off') {
            /* Endpoint gone: keep every operation queued for when it is back. */
            return false;
          }
          if (result.why === 'signed-out') {
            /* Identity changed under us (logged out in another tab). Dropping the
             * queue is the safe move: sending it under the next session would put
             * this shopper's items on someone else's account. */
            setQueue([]);
            emit();
            return false;
          }
          /* Transient: give it a few more goes before parking the queue. */
          var attempts = (op.attempts || 0) + 1;
          var ops2 = queue().map(function (entry) {
            if (entry.productId !== op.productId) return entry;
            entry.attempts = attempts;
            return entry;
          });
          setQueue(attempts >= MAX_ATTEMPTS ? ops2.filter(function (entry) {
            return entry.productId !== op.productId;
          }) : ops2);
          return false;
        });
      }

      return Promise.resolve(next()).then(function (done) {
        flushing = false;
        emit();
        return !!done;
      });
    }

    /* What the UI should show, given everything we know right now:
     *   - the guest list,
     *   - the account list (Liquid's render or the endpoint's answer),
     *   - this device's cache of it.
     * The account list is only merged into when it is KNOWN; when every source
     * failed we render the cache and wait, because merging into an unknown list is
     * how items end up on the wrong account. */
    function resolve(localEntries, accountItems) {
      var local = core.sanitise(localEntries || []);
      var remote = null;
      if (Array.isArray(accountItems)) remote = core.sanitiseAccountList(accountItems);
      else if (Array.isArray(state.account)) remote = core.sanitiseAccountList(state.account);

      if (mode !== 'account') return { items: local, added: [], remote: null, merge: false };
      if (!remote) {
        /* The account list is not known yet — but its REMOVALS may be, because
         * Liquid renders those beside the list. A row they block is not drawn
         * even for the first frame: waiting for the sync to say so is how a
         * shopper sees an item they deleted flash back onto the page. */
        var kept = function (row) { return !core.blockedByRemoval(removals, row); };
        var cache = cached().filter(kept);
        var fresh = local.filter(kept);
        return { items: cache.length ? cache : fresh, added: [], remote: null, merge: false };
      }
      var result = core.mergeLists(local, remote, removals);
      return { items: result.items, added: result.added, remote: remote, merge: result.added.length > 0 };
    }

    /* ------------------------------------------------------------------- api */

    var api = {
      state: state,
      /* True when the account list is real (from Liquid or the endpoint). */
      accountKnown: function () { return Array.isArray(state.account); },
      /* The removals this client currently knows about, for the UI and tests. */
      removals: function () { return removals.slice(); },
      endpoint: endpoint,
      customerId: customerId,

      subscribe: function (fn) {
        listeners.push(fn);
        return function () {
          var index = listeners.indexOf(fn);
          if (index !== -1) listeners.splice(index, 1);
        };
      },

      load: load,
      flush: flush,

      /* Merge the guest list into the account once, then tell the server which
       * items it was missing. Called with the list the UI is about to render. */
      adopt: function (localEntries, accountItems) {
        var plan = resolve(localEntries, accountItems);
        if (mode !== 'account' || !plan.remote) return plan;
        /* This is the list the UI is about to draw and the account's membership is
         * known, so it is also the best cache this device can hold: next load
         * paints real titles and prices before any request finishes. */
        setCache(plan.items);
        if (plan.merge) {
          var batch = plan.added.map(function (id) {
            var entry = plan.items.filter(function (row) { return row.id === id; })[0];
            return core.accountItem(entry);
          });
          setQueue(core.queueOperation(queue(), { type: 'merge', items: batch, at: now() }));
          emit();
          scheduleFlush();
        }
        return plan;
      },

      add: function (entry) {
        if (mode !== 'account') return false;
        var item = core.accountItem(entry);
        if (!item) return false;
        if (!item.addedAt) item.addedAt = now();
        /* The shopper is tapping the heart NOW, and this device already knows the
         * account removed this product at a time the SERVER dated. That is a fact
         * about ordering, so the save is dated after the removal it is undoing.
         *
         * It matters because `addedAt` is a device clock: without this, a phone
         * whose clock runs a minute behind the server could tap the heart on an
         * item and have the save refused by a removal that, from the shopper's
         * point of view, happened before they tapped. It cannot resurrect
         * anything, either — a device that does NOT know the removal (an offline
         * queue being replayed) gets no bump and is still refused. */
        var removedAt = core.removedAtFor(removals, item.productId);
        if (removedAt && !(item.addedAt > removedAt)) item.addedAt = removedAt + 1;
        enqueue({ type: 'add', item: item, at: now() });
        scheduleFlush();
        return true;
      },

      remove: function (productId) {
        if (mode !== 'account') return false;
        enqueue({ type: 'remove', productId: core.idFromGid(productId), at: now() });
        scheduleFlush();
        return true;
      },

      /* The guest list was confirmed into the account: it is now a cache of the
       * account list, so the device stops holding a second copy that a later
       * shopper would inherit. */
      confirmMerge: function () {
        if (mode !== 'account') return;
        setMerged(true);
      },

      merged: function () {
        return merged();
      },

      /* Logout, or the identity changed. Nothing on the server is touched: the
       * account list stays in Shopify. Only this page's view of it is dropped. */
      reset: function () {
        mode = 'guest';
        state.mode = 'guest';
        state.account = null;
        state.removed = [];
        removals = [];
        state.pending = 0;
        memory = {};
      }
    };

    /* `mode` is read after every render, and `reset()` changes it, so it has to be
     * the live value rather than a copy taken when the client was built. */
    Object.defineProperty(api, 'mode', { get: function () { return mode; }, enumerable: true });
    return api;
  }

  return { create: create };
});
